/**
 * 10.9EO — RAID: orquestra o convite no HUD, a entrada/saida da arena, a conexao com o servidor,
 * o Tita, os caçadores da sala, o painel da raid e a recompensa. Fala com o Game so pela interface
 * RaidHost (o Game continua dono do player, do boss HUD e do mundo).
 */
import * as THREE from "three";
import { GameConfig } from "../config/GameConfig";
import { i18n } from "../i18n/i18n";
import { RaidNet, type RaidEndInfo, type RaidHpInfo, type RaidJoinInfo } from "../net/RaidNet";
import { RaidTitan } from "./RaidTitan";
import { RaidArena, RemoteHunters } from "./RaidScene";
import { fmtCountdown, nextRaid, type RaidSlot } from "./RaidSchedule";
import type { Boss } from "../boss/Boss";
import type { Player } from "../player/Player";
import type { TelegraphSystem } from "../fx/Telegraph";
import type { ParticleSystem } from "../fx/Particles";

export interface RaidReward { slot: string; win: boolean; rank: number; players: number; myDmg: number; total: number; suspect: boolean }

export interface RaidHost {
  scene: THREE.Scene;
  camera: THREE.Camera;
  player: Player;
  telegraph: TelegraphSystem;
  particles: ParticleSystem;
  uiRoot: HTMLElement;
  hudStack: () => HTMLElement | null;
  impulse: (s: number) => void;
  banner: (text: string, kind: "info" | "boss" | "danger", ms: number) => void;
  canEnter: () => boolean; // jogando, fora de tela/duelo
  playerDamage: () => number;
  playerName: () => string;
  classId: () => string;
  level: () => number;
  userId: () => Promise<string | null>;
  attachBoss: (b: Boss) => void;
  detachBoss: () => void;
  enterArena: (x: number, z: number, r: number) => void;
  leaveArena: () => void;
  setInput: (on: boolean) => void;
  alreadyClaimed: (slot: string) => boolean;
  grantReward: (r: RaidReward) => void;
}

type Phase = "idle" | "connecting" | "in" | "ending" | "respawn"; // 10.9GN — respawn: esperando o boss renascer (nao sai)

export class RaidController {
  private phase: Phase = "idle";
  private slot: RaidSlot | null = null;
  private net: RaidNet | null = null;
  private titan: RaidTitan | null = null;
  private readonly arena: RaidArena;
  private readonly remotes: RemoteHunters;
  private invite: HTMLButtonElement | null = null;
  private readonly panel: HTMLDivElement;
  private inviteTimer = 0;
  private posTimer = 0;
  private deadTimer = 0;
  private endTimer = 0;
  private respawnAt = 0; // 10.9GN — ms (relogio local) em que o boss renasce
  private gen = 0;
  private hp: RaidHpInfo | null = null;
  private myDmg = 0;
  private myTaken = 0; // 10.9GF — dano sofrido (reportado ao servidor)
  private lastHp = 0;
  private serverFail = 0; // raid sem servidor: nao insiste a cada frame

  constructor(private readonly h: RaidHost) {
    this.arena = new RaidArena(h.scene);
    this.remotes = new RemoteHunters(h.scene);
    this.panel = document.createElement("div");
    this.panel.className = "raid-panel";
    this.panel.hidden = true;
    h.uiRoot.appendChild(this.panel);
    this.bindPanelLeave(); // 10.9HE
  }

  /** Esta DENTRO da raid (o Game usa pra desligar mundo/spawner/portais/penalidade de morte). */
  get active(): boolean { return this.phase !== "idle"; }
  get enabled(): boolean { return !!GameConfig.raid.url; }

  // ------------------------------------------------------------------ convite no HUD
  private ensureInvite(): HTMLButtonElement | null {
    if (this.invite) return this.invite;
    const b = document.createElement("button");
    b.className = "raid-invite";
    b.hidden = true;
    b.addEventListener("click", () => { if (b.classList.contains("open")) this.enter(nextRaid()); });
    (this.h.hudStack() ?? this.h.uiRoot).appendChild(b); // 10.9HP — Thiago: de volta pra pilha do canto (onde estava)
    this.invite = b;
    return b;
  }

  private refreshInvite(): void {
    const b = this.ensureInvite();
    if (!b) return;
    if (!this.enabled || this.active) { b.hidden = true; return; }
    const now = Date.now();
    const s = nextRaid(now);
    const claimed = this.h.alreadyClaimed(`${s.slot}#0`); // 10.9GN — chave por geracao
    if (now >= s.lobby && now < s.end && !claimed) {
      b.hidden = false;
      b.classList.add("open");
      b.textContent = now < s.start ? i18n.t("raid.openLobby", { t: fmtCountdown(s.start - now) }) : i18n.t("raid.openLive");
    } else if (now < s.lobby && s.lobby - now <= GameConfig.raid.announceMin * 60_000) {
      b.hidden = false;
      b.classList.remove("open");
      b.textContent = i18n.t("raid.soon", { t: fmtCountdown(s.lobby - now) });
    } else {
      b.hidden = true;
    }
  }

  // ------------------------------------------------------------------ entrar / sair

  enter(s: RaidSlot): void {
    if (this.active || !this.enabled || !this.h.canEnter()) return;
    this.phase = "connecting";
    this.slot = s;
    this.myDmg = 0;
    this.hp = null;
    this.myTaken = 0; this.lastHp = this.h.player.health.current; this.deadTimer = 0; this.respawnAt = 0; this.gen = 0; // 10.9GN
    const a = GameConfig.raid.arena;
    this.arena.setVisible(true);
    this.h.enterArena(a.x, a.z + a.r - 4, a.r);
    this.panel.hidden = false;
    this.renderPanel();
    this.h.banner(i18n.t("raid.entering"), "boss", 2200);
    void this.connect(s);
  }

  private async connect(s: RaidSlot): Promise<void> {
    const id = await this.h.userId();
    if (!this.active || this.slot !== s) return;
    if (!id) { this.abort(i18n.t("raid.noCloud")); return; }
    this.net = new RaidNet(s.slot, {
      onJoined: (j) => this.onJoined(j),
      onHp: (hp) => this.onHp(hp),
      onEnd: (e) => this.onEnd(e),
      onRoom: (ps) => this.remotes.apply(ps),
      onLost: (why) => { if (this.phase !== "ending") this.abort(i18n.t("raid.lost", { why })); },
    });
    this.net.connect({ id, name: this.h.playerName(), cls: this.h.classId(), lvl: this.h.level(), pd: Math.round(this.h.playerDamage()) });
  }

  private onJoined(j: RaidJoinInfo): void {
    if (!this.net) return;
    if (this.titan) { this.h.detachBoss(); this.titan = null; } // 10.9GE — respawn: recria o Titã
    this.phase = "in";
    this.myDmg = j.myDmg;
    this.myTaken = 0; this.respawnAt = 0; this.gen = j.gen ?? 0; // 10.9GN
    if (this.slot) this.slot = { ...this.slot, lobby: j.lobby, start: j.start, end: j.end }; // 10.9GN — respawn muda os tempos
    this.lastHp = this.h.player.health.current; // 10.9GF
    this.titan = new RaidTitan(this.h.scene, {
      player: this.h.player, telegraph: this.h.telegraph, particles: this.h.particles,
      impulse: this.h.impulse, serverNow: () => this.net?.serverNow() ?? Date.now(),
    }, j.start, j.seed, j.hpMax, j.hp);
    this.h.attachBoss(this.titan);
    if (!j.verified) this.h.banner(i18n.t("raid.unverified"), "info", 3000);
  }

  private onHp(hp: RaidHpInfo): void {
    this.hp = hp;
    this.titan?.syncServer(hp.hp, hp.hpMax);
  }

  private onEnd(e: RaidEndInfo): void {
    if (this.phase === "ending" || this.phase === "respawn") return;
    const willRespawn = (e.respawnIn ?? 0) > 0;
    // 10.9HE — BUG (Thiago): "matamos o boss e ficamos presos". A fase "respawn" (10.9GN) deixava o
    // jogador na arena esperando o Tita renascer (ate 10 min). Agora acabou = SEMPRE volta pro jogo
    // depois do placar (6 s); se o Tita for renascer, o aviso diz quando e o botao RAID AO VIVO reabre.
    this.phase = "ending";
    this.endTimer = e.win ? 6 : 4;
    this.respawnAt = willRespawn ? Date.now() + (e.respawnIn ?? 0) : 0;
    this.titan?.finish(e.win);
    const s = this.slot!;
    this.h.banner(e.win ? i18n.t("raid.win", { rank: String(e.rank), n: String(e.players) }) : i18n.t("raid.fail"), e.win ? "boss" : "danger", 5000);
    if (willRespawn) window.setTimeout(() => this.h.banner(i18n.t("raid.backLater", { t: fmtCountdown(e.respawnIn ?? 0) }), "info", 4000), 5200);
    // 10.9GN — recompensa por slot#geracao: cada respawn derrotado paga de novo
    const key = `${s.slot}#${e.gen ?? this.gen}`;
    if (!this.h.alreadyClaimed(key)) {
      this.h.grantReward({ slot: key, win: e.win, rank: e.rank, players: e.players, myDmg: e.myDmg, total: e.total, suspect: e.suspect });
    }
    this.renderEnd(e);
  }

  /** Falhou antes de lutar (sem servidor, sem conta) — volta pro jogo sem drama. */
  private abort(msg: string): void {
    this.h.banner(msg, "danger", 3200);
    this.serverFail = 30;
    this.leave();
  }

  /** 10.9HE — botao "Sair" do painel (delegacao: o painel e redesenhado a cada 0,5 s). */
  private bindPanelLeave(): void {
    this.panel.addEventListener("click", (ev) => {
      const t = ev.target as HTMLElement | null;
      if (t && t.closest("[data-a=leave]") && this.active) this.leave();
    });
  }

  leave(): void {
    if (!this.active) return;
    this.net?.close();
    this.net = null;
    if (this.titan) this.h.detachBoss();
    this.titan = null;
    this.remotes.clear();
    this.arena.setVisible(false);
    this.panel.hidden = true;
    this.h.leaveArena();
    this.h.setInput(true);
    this.phase = "idle";
    this.slot = null;
  }

  /** Morte na raid: SEM penalidade — volta em alguns segundos na borda da arena. */
  onLocalDeath(): void {
    this.deadTimer = GameConfig.raid.respawnDelay;
    this.h.setInput(false);
    this.h.banner(i18n.t("raid.down", { s: String(GameConfig.raid.respawnDelay) }), "danger", 2200);
  }

  // ------------------------------------------------------------------ loop
  update(dt: number): void {
    this.inviteTimer -= dt;
    if (this.serverFail > 0) this.serverFail -= dt;
    if (this.inviteTimer <= 0) { this.inviteTimer = 0.5; this.refreshInvite(); if (this.active) this.renderPanel(); }
    if (!this.active) return;

    this.arena.update(dt);
    this.remotes.update(dt, this.h.camera);
    const net = this.net;
    if (net) {
      // dano causado ao Tita desde o ultimo frame -> pacote
      if (this.titan) { const d = this.titan.takePending(); if (d > 0) { net.addDamage(d); this.myDmg += d; } }
      // 10.9GF — dano SOFRIDO: queda da vida do jogador (ignora cura/respawn) -> reporta pro ranking
      const hpNow = this.h.player.health.current;
      if (hpNow < this.lastHp) { const t = this.lastHp - hpNow; this.myTaken += t; net.addTaken(t); }
      this.lastHp = hpNow;
      net.update(dt);
      this.posTimer -= dt;
      if (this.posTimer <= 0) {
        this.posTimer = 1 / GameConfig.raid.sendPosHz;
        const p = this.h.player;
        net.sendPos({
          name: this.h.playerName(), cls: this.h.classId(),
          x: +p.position.x.toFixed(2), z: +p.position.z.toFixed(2), f: +p.facingAngle.toFixed(2),
          hp: Math.round(p.health.current), mhp: Math.round(p.health.max), a: 0,
        });
      }
    }
    // mantem o caçador dentro do circulo da arena (o clamp do mundo e um quadrado)
    const a = GameConfig.raid.arena;
    const p = this.h.player.position;
    const dx = p.x - a.x, dz = p.z - a.z;
    const d = Math.hypot(dx, dz);
    const lim = a.r - 0.6;
    if (d > lim) { p.x = a.x + (dx / d) * lim; p.z = a.z + (dz / d) * lim; }

    if (this.deadTimer > 0) {
      this.deadTimer -= dt;
      if (this.deadTimer <= 0) {
        this.h.player.respawnAt(a.x, a.z + a.r - 4, 1);
        this.h.setInput(true);
      }
    }
    if (this.phase === "ending") {
      this.endTimer -= dt;
      if (this.endTimer <= 0) this.leave();
    }
    // 10.9GN — esperando o boss renascer: fica na arena (o servidor manda um 'joined' novo)
    if (this.phase === "respawn" && this.respawnAt > 0) {
      const left = this.respawnAt - Date.now();
      if (left > 0 && this.inviteTimer <= 0) this.statusLine(i18n.t("raid.respawnIn", { t: fmtCountdown(left) }));
    }
  }

  // ------------------------------------------------------------------ painel
  /** 10.9GN — sobrescreve a 1a linha do painel (contagem do respawn) sem redesenhar o ranking. */
  private statusLine(text: string): void {
    const first = this.panel.querySelector<HTMLElement>(".rp-line");
    if (first) first.textContent = text; else this.panel.innerHTML = `<div class="rp-line">${esc(text)}</div>`;
  }
  private renderPanel(): void {
    const s = this.slot;
    if (!s) return;
    const now = this.net?.serverNow() ?? Date.now();
    const hp = this.hp;
    const time = now < s.start ? i18n.t("raid.wakes", { t: fmtCountdown(s.start - now) }) : fmtCountdown(s.end - now);
    const online = hp ? hp.online : 0;
    this.panel.innerHTML = `
      <div class="rp-line"><b>⚔️ ${i18n.t("raid.title")}</b> · ${online} ${i18n.t("raid.online")} · ⏱ ${time} <button type="button" class="rp-leave" data-a="leave">✕ ${esc(i18n.t("raid.leaveBtn"))}</button></div>
      <div class="rp-line rp-sub">${i18n.t("raid.myDmg")}: <b>${fmtNum(this.myDmg)}</b> · 💔 ${fmtNum(this.myTaken)}</div>
      ${hp?.top?.length ? `<div class="rp-rank"><div class="rp-rank-h">⚔️ ${esc(i18n.t("raid.topDmg"))}</div>${hp.top.slice(0,3).map((t,i)=>`<div class="rp-row"><span>${i+1}. ${esc(t.name)}</span><b>${fmtNum(t.dmg)}</b></div>`).join("")}</div>` : ""}
      ${hp?.takenTop?.length ? `<div class="rp-rank"><div class="rp-rank-h">💔 ${esc(i18n.t("raid.topTaken"))}</div>${hp.takenTop.slice(0,2).map((t,i)=>`<div class="rp-row"><span>${i+1}. ${esc(t.name)}</span><b>${fmtNum(t.dmg)}</b></div>`).join("")}</div>` : ""}`;
  }

  private renderEnd(e: RaidEndInfo): void {
    const rows = e.top.slice(0, 5).map((t, i) => `<div class="rp-row"><span>${i + 1}. ${esc(t.name)}</span><b>${fmtNum(t.dmg)}</b></div>`).join("");
    this.panel.innerHTML = `
      <div class="rp-line"><b>${e.win ? "🏆 " + i18n.t("raid.victory") : "⌛ " + i18n.t("raid.timeUp")}</b></div>
      <div class="rp-line rp-sub">${i18n.t("raid.yourRank", { rank: String(e.rank), n: String(e.players) })} · ${fmtNum(e.myDmg)}</div>
      ${e.suspect ? `<div class="rp-line rp-warn">${i18n.t("raid.suspect")}</div>` : ""}
      <div class="rp-rank-h">⚔️ ${esc(i18n.t("raid.topDmg"))}</div><div class="rp-top">${rows}</div>
      ${e.takenTop?.length ? `<div class="rp-rank-h">💔 ${esc(i18n.t("raid.topTaken"))}</div><div class="rp-top">${e.takenTop.slice(0,5).map((t,i)=>`<div class="rp-row"><span>${i+1}. ${esc(t.name)}</span><b>${fmtNum(t.dmg)}</b></div>`).join("")}</div>` : ""}`;
  }
}

function fmtNum(n: number): string {
  const v = Math.round(n);
  if (v >= 1e9) return (v / 1e9).toFixed(1) + "B";
  if (v >= 1e6) return (v / 1e6).toFixed(1) + "M";
  if (v >= 1e3) return (v / 1e3).toFixed(1) + "K";
  return String(v);
}
function esc(s: string): string { return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }
